/**
 * Support messaging — frontend model.
 *
 * Mirrors `backend/functions/src/support/types.ts` (no shared package, no
 * codegen — the house convention). Two differences are deliberate:
 *
 *  - timestamps are hydrated to `Date` on read, the way `WorkspaceModel` and
 *    `SentCommunication` do it, so templates can format them without a pipe
 *    that understands Firestore `Timestamp`;
 *  - the limits are re-declared rather than imported: they drive maxlength
 *    attributes and counters, and a UI that silently disagreed with the server
 *    would show a form that cannot be submitted.
 */

export type SupportLang = 'en' | 'fr' | 'nl' | 'de';

export type SupportAudience = 'proporio' | 'workspace';

export type SupportStatus = 'open' | 'answered' | 'closed';

export type SupportMessageRole = 'user' | 'staff' | 'system';

export type SupportMessageSource = 'app' | 'slack' | 'email' | 'public_form';

/**
 * Technical context attached to a ticket, with the user's consent.
 *
 * Shown verbatim behind the "see what is sent" disclosure in the form: the
 * consent is only meaningful if what is being consented to is legible.
 */
export interface SupportContext {
  route?: string;
  workspaceType?: string;
  plan?: string;
  buildVersion?: string;
  userAgent?: string;
  consoleErrors?: string[];
  conversationId?: string;
}

export interface SupportAttachment {
  path: string;
  name: string;
  size: number;
  mime: string;
}

export interface SupportThread {
  id: string;
  audience: SupportAudience;
  status: SupportStatus;
  subject: string;
  email: string;
  displayName: string;
  locale: SupportLang;
  userId: string | null;
  workspaceId: string | null;
  context: SupportContext | null;
  unreadForUser: boolean;
  messageCount: number;
  createdAt: Date | null;
  updatedAt: Date | null;
  lastMessageAt: Date | null;
  lastStaffReplyAt: Date | null;
}

export interface SupportMessage {
  id: string;
  role: SupportMessageRole;
  authorId: string | null;
  authorName: string;
  content: string;
  source: SupportMessageSource;
  attachments: SupportAttachment[] | null;
  bodyTranslations: Partial<Record<SupportLang, { text: string; translatedAt: string }>> | null;
  createdAt: Date | null;
  /**
   * Set when a staff reply never reached the ticket's Slack thread. Read only
   * by the admin console — `slackTs` stays backend-only, but this one has to
   * cross: the person who wrote the reply is the one who needs to know the
   * channel did not get it.
   *
   * Optional because the tenant-side store deliberately does not map it: the
   * user has no business knowing whether our channel got its copy. Both
   * mappers cast with `as SupportMessage`, so a required field here would
   * silently be `undefined` in the panel rather than fail the build.
   */
  slackMirrorFailedAt?: Date | null;
}

export const SUPPORT_LIMITS = {
  subjectMaxLength: 200,
  messageMaxLength: 5000,
  displayNameMaxLength: 100,
  maxMessagesPerThread: 200,
  maxContextErrors: 10,
  maxContextErrorLength: 500,
  maxAttachments: 3
} as const;

/**
 * Firestore hands back `Timestamp | Date | null` depending on whether the write
 * has been acknowledged by the server yet (a pending `serverTimestamp()` reads
 * as `null` in the local snapshot). One helper rather than a guard at every
 * call site.
 *
 * @param value Raw value straight off a Firestore document.
 * @returns A `Date`, or `null` when the server has not stamped it yet.
 */
export function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  const ts = value as { toDate?: () => Date };
  if (typeof ts.toDate === 'function') return ts.toDate();
  if (typeof value === 'number') return new Date(value);
  return null;
}
